import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  roleHasPermission,
  type AuthUser,
  type OnboardingSession,
  type Permission,
  type RegisterCompany,
  type Role,
} from '@arriba.digital/contracts';
import { authApi } from '../api/resources';
import { onUnauthorized } from '../api/client';
import { session } from '../api/session';

/**
 * Authentication state for the whole SPA.
 *
 * The server is the authority on permissions: `GET /auth/me` returns the
 * effective permission list for the signed-in user, and `can()` consults that
 * list rather than re-deriving it from the role locally. The role-based
 * helpers exist only to drive navigation and hide controls that would fail
 * anyway — every one of them is enforced again on the API.
 */

export interface LoginCredentials {
  email: string;
  password: string;
}

/**
 * What a password check came back with.
 *
 * Signing in is a two-step conversation when the account has the e-mailed
 * code enabled, so `login` cannot simply return a user: it reports either a
 * finished session or the challenge that has to be completed.
 */
export type LoginOutcome =
  | { status: 'authenticated'; user: AuthUser }
  | { status: 'two-factor'; challengeId: string; emailHint: string };

interface AuthContextValue {
  user: AuthUser | null;
  permissions: Permission[];
  status: 'loading' | 'authenticated' | 'anonymous';
  login: (credentials: LoginCredentials) => Promise<LoginOutcome>;
  /** Complete a two-factor sign-in with the four-digit code. */
  verifyTwoFactor: (challengeId: string, code: string) => Promise<AuthUser>;
  /**
   * Create an account. Lands nowhere yet: the answer is a ticket to choose a
   * country, and only that choice produces a workspace and a session.
   */
  register: (payload: RegisterCompany) => Promise<OnboardingSession>;
  /** Choose the country, which builds the workspace and signs the owner in. */
  chooseCountry: (ticket: string, country: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  can: (permission: Permission) => boolean;
  hasRole: (...roles: Role[]) => boolean;
  /** Turn the e-mailed sign-in code on or off for the current user. */
  setTwoFactor: (enabled: boolean) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<AuthUser | null>(() => session.user);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [status, setStatus] = useState<'loading' | 'authenticated' | 'anonymous'>(() =>
    session.isAuthenticated ? 'loading' : 'anonymous',
  );

  const clear = useCallback(() => {
    session.clear();
    setUser(null);
    setPermissions([]);
    setStatus('anonymous');
    queryClient.clear();
  }, [queryClient]);

  /** Validate the stored token and load the effective permission list. */
  const refresh = useCallback(async () => {
    if (!session.isAuthenticated) {
      clear();
      return;
    }

    try {
      const me = await authApi.me();
      const { permissions: effective, ...profile } = me;

      setUser(profile);
      setPermissions(effective as Permission[]);
      session.setUser(profile);
      setStatus('authenticated');
    } catch {
      clear();
    }
  }, [clear]);

  // Revalidate once on boot, so a revoked account or a renamed user does not
  // linger in the UI until the next request happens to fail.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  // The API client notifies us when a refresh attempt failed for good.
  useEffect(
    () =>
      onUnauthorized(() => {
        setUser(null);
        setPermissions([]);
        setStatus('anonymous');
        queryClient.clear();
      }),
    [queryClient],
  );

  /**
   * Adopt a freshly issued token pair and pull the permission list.
   *
   * Shared by password sign-in, two-factor sign-in and choosing a country,
   * because all three end in exactly the same state.
   */
  const adoptSession = useCallback(
    async (response: { accessToken: string; refreshToken: string; user: AuthUser }) => {
      /*
       * Check before adopting.
       *
       * The three endpoints that call this — sign-in, the two-factor step and
       * choosing a country — are the only places a session is created, and
       * each one is a different server route. A client that is out of step
       * with the API can be handed something that is not a session: the
       * registration ticket is exactly that, and it is what a stale build
       * adopted here, producing a stored `"undefined"` and a redirect to the
       * login screen with no explanation.
       *
       * Failing here instead turns a silent, confusing sign-out into a
       * message that says what happened.
       */
      if (
        typeof response?.accessToken !== 'string' ||
        response.accessToken === '' ||
        typeof response?.refreshToken !== 'string' ||
        response.refreshToken === '' ||
        !response.user
      ) {
        session.clear();
        setUser(null);
        setPermissions([]);
        setStatus('anonymous');
        throw new Error('errors.session.malformed');
      }

      session.setTokens(response.accessToken, response.refreshToken);
      session.setUser(response.user);
      setUser(response.user);
      setStatus('authenticated');

      // The login payload carries only the role; the permission list is
      // derived server-side.
      try {
        const me = await authApi.me();
        setPermissions(me.permissions as Permission[]);
      } catch {
        // Non-fatal: `can()` falls back to the role below.
        setPermissions([]);
      }

      return response.user;
    },
    [],
  );

  const login = useCallback(
    async (credentials: LoginCredentials): Promise<LoginOutcome> => {
      const result = await authApi.login(credentials);

      if ('twoFactorRequired' in result) {
        return {
          status: 'two-factor',
          challengeId: result.challengeId,
          emailHint: result.emailHint,
        };
      }

      return { status: 'authenticated', user: await adoptSession(result) };
    },
    [adoptSession],
  );

  const verifyTwoFactor = useCallback(
    async (challengeId: string, code: string) => {
      const response = await authApi.verifyTwoFactor({ challengeId, code });
      return adoptSession(response);
    },
    [adoptSession],
  );

  const register = useCallback(async (payload: RegisterCompany) => {
    // No session is adopted here on purpose. Registration writes the account
    // and reserves a workspace name; the country chosen on the next screen is
    // what decides the tax rules, the currency and the numbering that get
    // seeded, so the database — and the session — wait for it.
    return authApi.register(payload);
  }, []);

  const chooseCountry = useCallback(
    async (ticket: string, country: string) => {
      const response = await authApi.chooseCountry(ticket, { country });
      return adoptSession(response);
    },
    [adoptSession],
  );

  const setTwoFactor = useCallback(async (enabled: boolean) => {
    const result = await authApi.setTwoFactor(enabled);

    setUser((current) => {
      if (!current) {
        return current;
      }
      const updated = { ...current, twoFactorEnabled: result.enabled };
      session.setUser(updated);
      return updated;
    });
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // The local session is cleared regardless: a failed logout call must
      // never leave the user signed in on the device.
    }
    clear();
  }, [clear]);

  const can = useCallback(
    (permission: Permission) => {
      if (permissions.length > 0) {
        return permissions.includes(permission);
      }
      // Before /auth/me resolves, fall back to the role so the navigation
      // does not flicker.
      return user ? roleHasPermission(user.role, permission) : false;
    },
    [permissions, user],
  );

  const hasRole = useCallback(
    (...roles: Role[]) => (user ? roles.includes(user.role) : false),
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      permissions,
      status,
      login,
      verifyTwoFactor,
      register,
      chooseCountry,
      logout,
      refresh,
      can,
      hasRole,
      setTwoFactor,
    }),
    [
      user,
      permissions,
      status,
      login,
      verifyTwoFactor,
      register,
      chooseCountry,
      logout,
      refresh,
      can,
      hasRole,
      setTwoFactor,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return context;
}
